// web/src/components/loader/RiveMeter.tsx
// The processing progress meter: the ProgMeter Rive artboard (auto-bound "Progress" VM),
// driven by `charge`. Replaces the temporary DOM dot-tracker. We push three bound props via
// a rAF loop (charge read from a ref → no re-render): meterprogress (0–max), the stage enum
// (Intro/Analyze/Roast/Sauce/Ignite), and meterColor — which "heats up" blue→yellow→orange→red
// as it charges, then FLASHES red↔brighter-red at the end. Mirrors RoastCharacter's VM pattern.
//
// Two layouts: the wide desktop artboard `ProgMeter` (max 583) and a narrower portrait
// `ProgMeterMobile` (max 400) for phones, selected by a viewport breakpoint. Both artboards
// share the SAME state machine (SM_Progress) and the SAME bound VM props
// (meterprogress/stage/meterColor) — only their internal `max` + section boundaries differ.
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useRive, Layout, Fit, Alignment } from "@rive-app/react-webgl2";

// `?v=` cache-busters injected by vite.config's `define` (content hash of each .riv). Guarded
// with typeof so the module still loads under vitest, which uses a separate config with no
// define — there the identifier is replaced by nothing and typeof safely reads "undefined".
const DESKTOP_VER = typeof __PROGMETER_VER__ !== "undefined" ? __PROGMETER_VER__ : "dev";
const MOBILE_VER = typeof __PROGMETER_MOBILE_VER__ !== "undefined" ? __PROGMETER_MOBILE_VER__ : "dev";

/** A meter layout variant = one artboard + its internal progress scale + stage boundaries
 *  (in meterprogress units). The stage flips happen at ~the same CHARGE percentage across
 *  both — the artboards just use different internal maxima. */
interface MeterVariant {
  /** Which .riv to load (desktop + mobile artboards live in separate Rive documents). */
  src: string;
  artboard: string;
  stateMachine: string;
  max: number;
  /** meterprogress value at which the stage enum flips INTO each stage (ascending). */
  thresholds: { analyze: number; roast: number; sauce: number; ignite: number };
}

// Desktop: ProgMeter, max 583. Sections: Intro 0–30, Analyze 30–196, Roast 196–363,
// Sauce 363–536, Ignite 536–583.
export const DESKTOP_METER: MeterVariant = {
  src: `/ui/progmeter.riv?v=${DESKTOP_VER}`,
  artboard: "ProgMeter",
  stateMachine: "SM_Progress",
  max: 583,
  thresholds: { analyze: 30, roast: 196, sauce: 363, ignite: 536 },
};

// Mobile: ProgMeterMobile, max 400. Sections (from the artboard): Roast starts 136,
// Sauce 250, Ignite 362, Full 400. Intro→Analyze kept proportional to desktop (~5% → 20).
// Lives in its OWN Rive document (progmeter_v2.riv — mobile-only, same SM_Progress + stage
// enum + meterprogress/meterColor VM props as the desktop artboard).
export const MOBILE_METER: MeterVariant = {
  src: `/ui/progmeter_v2.riv?v=${MOBILE_VER}`,
  artboard: "ProgMeterMobile",
  stateMachine: "SM_Progress",
  max: 400,
  thresholds: { analyze: 20, roast: 136, sauce: 250, ignite: 362 },
};

// Phones + narrow windows get the mobile artboard. Width-based ("mobile breakpoint") rather
// than pointer-based because the swap is purely about the narrow portrait layout.
const MOBILE_METER_QUERY = "(max-width: 768px)";

// charge (0–100) → stage enum, keyed to the variant's SECTION boundaries in meterprogress.
export function stageFor(charge: number, v: MeterVariant): string {
  const mp = (Math.max(0, Math.min(100, charge)) / 100) * v.max;
  if (mp >= v.thresholds.ignite) return "Ignite";
  if (mp >= v.thresholds.sauce) return "Sauce";
  if (mp >= v.thresholds.roast) return "Roast";
  if (mp >= v.thresholds.analyze) return "Analyze";
  return "Intro";
}

// One DISCRETE color per stage — the meter snaps to it when the stage enum flips (no gradient).
const STAGE_RGB: Record<string, [number, number, number]> = {
  Intro: [0x53, 0xa8, 0xff], // #53a8ff blue
  Analyze: [0x53, 0xa8, 0xff], // #53a8ff blue
  Roast: [0xff, 0xd2, 0x3f], // yellow
  Sauce: [0xff, 0x8c, 0x1a], // orange
  Ignite: [0xff, 0x2a, 0x2a], // red (flashes at the very end)
};

/** Reactively pick the meter variant for the current viewport (re-evaluates on resize/rotate). */
function useMeterVariant(): MeterVariant {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_METER_QUERY).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(MOBILE_METER_QUERY);
    const onChange = (): void => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);
  return isMobile ? MOBILE_METER : DESKTOP_METER;
}

export function RiveMeter({ charge, phase }: { charge: number; phase: "charging" | "ignite" }): JSX.Element {
  const variant = useMeterVariant();
  // Key on the artboard so crossing the breakpoint remounts the canvas — useRive binds the
  // artboard at init and doesn't hot-swap it on an option change.
  return <MeterCanvas key={variant.artboard} variant={variant} charge={charge} phase={phase} />;
}

function MeterCanvas({
  variant, charge, phase,
}: {
  variant: MeterVariant;
  charge: number;
  phase: "charging" | "ignite";
}): JSX.Element {
  const { rive, RiveComponent } = useRive({
    src: variant.src,
    artboard: variant.artboard,
    stateMachines: variant.stateMachine, // applies the bound VM props (meterprogress/stage/meterColor) to the visual
    autoBind: true,
    autoplay: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.BottomCenter }),
  });

  const chargeRef = useRef(charge);
  chargeRef.current = charge;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const variantRef = useRef(variant);
  variantRef.current = variant;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = useRef<{ progress: any; stage: any; color: any } | null>(null);

  // Grab the bound VM property handles once the scene loads (guard each — a VM may omit some).
  useEffect(() => {
    const vmi = rive?.viewModelInstance;
    if (!vmi) return;
    props.current = { progress: vmi.number("meterprogress"), stage: vmi.enum("stage"), color: vmi.color("meterColor") };
  }, [rive]);

  // Keep the WebGL drawing surface sized to its container (mirrors RoastCharacter).
  useEffect(() => {
    if (!rive) return;
    let raf = 0;
    const onResize = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => rive.resizeDrawingSurfaceToCanvas(Math.min(Math.max(window.devicePixelRatio || 1, 1), 3)));
    };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, [rive]);

  // Drive the bound props from charge every frame; flash red at the end.
  useEffect(() => {
    let raf = 0;
    let lastStage = "";
    const loop = (t: number): void => {
      raf = requestAnimationFrame(loop);
      const p = props.current;
      if (!p) return;
      const c = chargeRef.current;
      const v = variantRef.current;
      if (p.progress) {
        const val = (Math.max(0, Math.min(100, c)) / 100) * v.max;
        if (p.progress.value !== val) p.progress.value = val;
      }
      const s = stageFor(c, v);
      if (p.stage && s !== lastStage) { try { p.stage.value = s; lastStage = s; } catch { /* invalid enum value */ } }
      if (p.color) {
        let [r, g, b] = STAGE_RGB[s] ?? STAGE_RGB.Intro;
        if (c >= 97 || phaseRef.current === "ignite") {
          const f = 0.5 + 0.5 * Math.sin(t / 55); // ~fast pulse: red ↔ brighter red
          r = 255;
          g = Math.round(38 + (120 - 38) * f);
          b = Math.round(38 + (96 - 38) * f);
        }
        p.color.rgb(r, g, b);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <RiveComponent className="h-full w-full" />;
}
